import React from "react";
import { motion } from "framer-motion";
import { Copy, Check } from "lucide-react";
import { CA, CATS, PUMP_URL, X_URL, TELEGRAM_URL, TIKTOK_URL, ASSETS } from "../../data/pusheen";
import { useSound } from "../../sound/SoundContext";
import { burst } from "../../lib/burst";
import { toast } from "sonner";

export default function Footer() {
  const { play } = useSound();
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(CA); } catch {}
    setCopied(true);
    play("sparkle");
    burst(window.innerWidth / 2, window.innerHeight - 120, { count: 14, kinds: ["heart"] });
    toast.custom(() => (
      <div className="flex items-center gap-3 bg-pusheen-cream border-[3px] border-pusheen-brown rounded-2xl px-4 py-3 shadow-[4px_4px_0_#4A3728]">
        <img src={CATS.heart} alt="" className="w-9 h-9" />
        <p className="font-display font-bold text-pusheen-brown">Copied! 💖</p>
      </div>
    ), { duration: 2000 });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <footer className="relative z-10 pt-10 pb-14 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white border-[3px] border-pusheen-brown rounded-[2rem] shadow-[6px_6px_0_#4A3728] p-8 sm:p-12 text-center flex flex-col items-center">
          <img src={CATS.wave} alt="Pusheen waving goodbye" className="w-28 animate-bob mb-4" draggable="false" />
          <h2 className="font-script text-4xl sm:text-5xl text-pusheen-brown">The legend continues.</h2>
          <p className="font-body text-pusheen-brownlight mt-2 max-w-md">Join the community celebrating one of the internet&apos;s most beloved characters.</p>

          <div className="flex flex-wrap items-center justify-center gap-3 mt-7">
            <a
              href={PUMP_URL} target="_blank" rel="noopener noreferrer" onClick={() => play("yay")}
              data-testid="footer-buy-button"
              className="bg-pusheen-pink text-pusheen-brown border-[3px] border-pusheen-brown rounded-full px-8 py-3 font-display font-extrabold text-lg shadow-[4px_4px_0_#4A3728] hover:bg-pusheen-pinklight hover:-translate-y-1 active:translate-y-[4px] active:shadow-none transition-all"
            >
              Buy $PUSHEEN
            </a>
            <a
              href={X_URL} target="_blank" rel="noopener noreferrer" onClick={() => play("pop")}
              data-testid="footer-x-button"
              className="bg-white text-pusheen-brown border-[3px] border-pusheen-brown rounded-full px-6 py-3 font-display font-extrabold text-lg shadow-[4px_4px_0_#4A3728] hover:bg-pusheen-cream hover:-translate-y-1 active:translate-y-[4px] active:shadow-none transition-all inline-flex items-center gap-2"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              @pusheencatfans
            </a>
            <a
              href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" onClick={() => play("pop")}
              data-testid="footer-telegram-button"
              className="bg-white text-pusheen-brown border-[3px] border-pusheen-brown rounded-full px-6 py-3 font-display font-extrabold text-lg shadow-[4px_4px_0_#4A3728] hover:bg-pusheen-cream hover:-translate-y-1 active:translate-y-[4px] active:shadow-none transition-all inline-flex items-center gap-2"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
              </svg>
              Telegram
            </a>
            <a
              href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" onClick={() => play("pop")}
              data-testid="footer-tiktok-button"
              className="bg-white text-pusheen-brown border-[3px] border-pusheen-brown rounded-full px-6 py-3 font-display font-extrabold text-lg shadow-[4px_4px_0_#4A3728] hover:bg-pusheen-cream hover:-translate-y-1 active:translate-y-[4px] active:shadow-none transition-all inline-flex items-center gap-2"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
              </svg>
              TikTok
            </a>
          </div>

          <button
            onClick={copy} data-testid="footer-copy-ca"
            className="mt-6 group inline-flex items-center gap-2 bg-pusheen-mint border-2 border-pusheen-brown rounded-full px-4 py-2 font-mono text-xs text-pusheen-brown hover:-translate-y-0.5 transition-transform max-w-full"
          >
            <span className="truncate max-w-[220px] sm:max-w-none">{CA}</span>
            {copied ? <Check className="w-4 h-4 shrink-0" /> : <Copy className="w-4 h-4 shrink-0" />}
          </button>
        </div>

        <div className="mt-8 flex flex-col items-center gap-2">
          <img src={ASSETS.logo} alt="Pusheen the Cat" className="h-8 opacity-80" />
          <p className="font-body text-xs text-pusheen-brownlight/80 text-center max-w-lg">
            A community-made fan tribute &amp; meme. $PUSHEEN is not an investment. Do your own research, be kind, stay chonky. 🐾
          </p>
        </div>
      </div>
    </footer>
  );
}
